import React, { useState, useEffect } from 'react';
import { Button, Box, Typography, Container } from '@mui/material';
import { styled } from '@mui/system';
import PopoverCard from './PopoverCard';
import BaseButton from './BaseButton';

let audioCtx = null;
let ringBuffer = null;

const getAudioContext = () => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  return audioCtx;
};

const loadRingBuffer = async () => {
  if (ringBuffer) return ringBuffer;
  const ctx = getAudioContext();
  const response = await fetch('/sounds/ring.mp3');
  const arrayBuffer = await response.arrayBuffer();
  ringBuffer = await ctx.decodeAudioData(arrayBuffer);
  return ringBuffer;
};

const playRing = async () => {
  try {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') await ctx.resume();
    const buffer = await loadRingBuffer();
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);
    source.start();
  } catch (e) {
    console.error('Error playing sound:', e);
  }
};

const CustomContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  flexDirection: 'column',

});


const CountdownTimer = ({ duration, toggleTimer, handleCountdownOver, onGiveUp, playerOne, playerTwo, hideGiveUp, gameMode, onGiveUpReplay }) => {
  const [isTimerRunning, setIsTimerRunning] = useState(true);
  const [timeLeft, setTimeLeft] = useState(duration);
  const [preTimer, setPreTimer] = useState(3); // 3, 2, 1 countdown
  const [pausedPopover, setPausedPopover] = useState(false)
  const [giveUpPopover, setGiveUpPopover] = useState(false)
  const [restartConfirmPopover, setRestartConfirmPopover] = useState(false)
  const [showButtons, setShowButtons] = useState(true)
  const [pickedGiveUpPlayer, setPickedGiveUpPlayer] = useState(null)
  const [replayGiveUpConfirmOpen, setReplayGiveUpConfirmOpen] = useState(false)

  useEffect(() => {
    let preTimerId;
    let timerId;

    if (isTimerRunning) {
      if (preTimer > 0) {
        preTimerId = setInterval(() => {
          setPreTimer((prevTime) => prevTime - 1);
        }, 1000);

        return () => clearInterval(preTimerId);
      } else {
        playRing();
        timerId = setInterval(() => {
          setTimeLeft((prevTime) => {
            if (prevTime > 0) {
              return prevTime - 1;
            } else {
              clearInterval(timerId);
              playRing();
              setShowButtons(false)
              handleCountdownOver()
              return 0;
            }
          });
        }, 1000);

        return () => clearInterval(timerId);
      }
    }

      return () => {
        if (preTimerId) clearInterval(preTimerId);
        if (timerId) clearInterval(timerId);;
      }
  }, [preTimer, isTimerRunning]);

  const resetClock = (time) => {
    setPreTimer(3)
    setTimeLeft(time)
    setShowButtons(true)
    setIsTimerRunning(true)
    if (toggleTimer) toggleTimer()
  }

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = time % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const handlePause = () => {
    if (timeLeft === 0) {
      setRestartConfirmPopover(true);
      return;
    }
    setIsTimerRunning(false)
    setPausedPopover(true)
  }
  
  const handleUnpause = () => {
    setPausedPopover(false)
    setPreTimer(3)
    setIsTimerRunning(true)
  }

  const handleGiveUp = ()=>{
    setIsTimerRunning(false)
    setGiveUpPopover(true)
  }

  const handleCloseGiveUp = ()=>{
    setGiveUpPopover(false)
    setPreTimer(3)
    setIsTimerRunning(true)
  }

  const svgSize = 268;
  const svgCenter = svgSize / 2;
  const ringRadius = 122;
  const circumference = 2 * Math.PI * ringRadius;
  const progress = preTimer > 0 || duration === 0 ? 0 : (1 - timeLeft / duration);
  const strokeDashoffset = circumference * progress;
  const dotAngle = (preTimer > 0 || duration === 0 ? 1 : timeLeft / duration) * 2 * Math.PI;
  const dotX = svgCenter + ringRadius * Math.cos(dotAngle);
  const dotY = svgCenter + ringRadius * Math.sin(dotAngle);

  return (
    <CustomContainer >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          paddingBottom: '20px',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: `${svgSize}px`,
            height: `${svgSize}px`,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            animation: preTimer === 0
              ? (timeLeft === 0 ? 'timerFadeOut 1.8s ease-out forwards' : 'timerPulse 4s ease-in-out infinite')
              : 'none',
          }}
          onClick={handlePause}
        >
          <style>{`
            @keyframes timerPulse {
              0%   { filter: drop-shadow(0 0 6px rgba(113, 24, 55, 0.35)); }
              45%  { filter: drop-shadow(0 0 28px rgba(197, 68, 156, 0.7)); }
              80%  { filter: drop-shadow(0 0 20px rgba(113, 24, 55, 0.55)); }
              100% { filter: drop-shadow(0 0 6px rgba(113, 24, 55, 0.35)); }
            }
            @keyframes timerFadeOut {
              0%   { filter: drop-shadow(0 0 22px rgba(197, 68, 156, 0.6)); }
              100% { filter: drop-shadow(0 0 0px rgba(197, 68, 156, 0)); }
            }
            @keyframes fillPulse {
              0%, 100% { opacity: 0.75; }
              50%       { opacity: 1; }
            }
          `}</style>

          {/* SVG progress ring */}
          <svg
            width={svgSize}
            height={svgSize}
            style={{ position: 'absolute', top: 0, left: 0, transform: 'rotate(-90deg)' }}
          >
            <defs>
              <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#711837" />
                <stop offset="55%" stopColor="#C5449C" />
                <stop offset="100%" stopColor="#f0a0c8" />
              </linearGradient>
              <filter id="ringGlow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3.5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <filter id="dotGlow" x="-150%" y="-150%" width="400%" height="400%">
                <feGaussianBlur stdDeviation="5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Faint background track */}
            <circle
              cx={svgCenter}
              cy={svgCenter}
              r={ringRadius}
              fill="none"
              stroke="rgba(113, 24, 55, 0.22)"
              strokeWidth="5"
            />

            {/* Progress arc */}
            <circle
              cx={svgCenter}
              cy={svgCenter}
              r={ringRadius}
              fill="none"
              stroke="url(#ringGradient)"
              strokeWidth="5"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              filter="url(#ringGlow)"
              style={{ transition: 'stroke-dashoffset 1s linear' }}
            />

            {/* Glowing dot at the leading edge */}
            {preTimer === 0 && timeLeft > 0 && (
              <circle
                cx={dotX}
                cy={dotY}
                r="5"
                fill="#ffccd9"
                filter="url(#dotGlow)"
              />
            )}
          </svg>

          {/* Inner dark circle */}
          <Box
            sx={{
              position: 'relative',
              width: '212px',
              height: '212px',
              borderRadius: '50%',
              background: 'linear-gradient(145deg, #1e0916, #0d0712)',
              boxShadow: 'inset 0 0 18px rgba(0, 0, 0, 0.55), inset 0 0 36px rgba(113, 24, 55, 0.18)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1,
              overflow: 'hidden',
            }}
          >
            {/* Color fill that rises from the bottom as time passes */}
            <Box sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '50%',
              background: `
                radial-gradient(ellipse at center bottom,
                  rgba(113, 24, 55, ${(progress * 0.88).toFixed(2)}),
                  rgba(113, 24, 55, 0) 72%
                ),
                radial-gradient(ellipse at 70% 100%,
                  rgba(197, 68, 156, ${(progress * 0.6).toFixed(2)}),
                  rgba(140, 40, 95, 0) 42%
                ),
                radial-gradient(ellipse at 30% 100%,
                  rgba(197, 68, 156, ${(progress * 0.6).toFixed(2)}),
                  rgba(140, 40, 95, 0) 42%
                )
              `,
              animation: 'fillPulse 5s ease-in-out infinite',
              filter: 'blur(1px)',
              pointerEvents: 'none',
            }} />

            <Typography sx={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: preTimer > 0 ? '5rem' : '3.4rem',
              fontWeight: 600,
              letterSpacing: preTimer > 0 ? '0' : '0.04em',
              color: '#ffbfe5',
              textShadow: '0 0 18px rgba(212, 138, 174, 0.55)',
              zIndex: 1,
              lineHeight: 1,
              marginBottom: preTimer > 0 ? 3 : 0,
              transition: 'font-size 0.2s ease',
            }}>
              {preTimer > 0 ? preTimer : formatTime(timeLeft)}
            </Typography>

            {preTimer === 0 && timeLeft > 0 && (
              <Typography sx={{
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: '0.65rem',
                letterSpacing: '0.22em',
                color: 'rgba(212, 138, 174, 0.4)',
                textTransform: 'uppercase',
                marginTop: '8px',
                zIndex: 1,
              }}>
                tap to pause
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <PopoverCard
      mainText={"Game Paused"}
      open={pausedPopover}
      onClose = {handleUnpause}/>
      <PopoverCard
      mainText={gameMode === 'replay' ? "Who decided to stop?" : "Who Should Take The Shot..?"}
      open={giveUpPopover}
      onClose={handleCloseGiveUp}
      showButtonOne={true}
      textButtonOne={playerOne.name + "!"}
      onButtonOne={() => {
        if (gameMode === 'replay') {
          setPickedGiveUpPlayer(playerOne);
          setGiveUpPopover(false);
          setReplayGiveUpConfirmOpen(true);
        } else {
          onGiveUp(playerOne);
        }
      }}
      showButtonTwo={true}
      textButtonTwo={playerTwo.name + "!"}
      onButtonTwo={() => {
        if (gameMode === 'replay') {
          setPickedGiveUpPlayer(playerTwo);
          setGiveUpPopover(false);
          setReplayGiveUpConfirmOpen(true);
        } else {
          onGiveUp(playerTwo);
        }
      }}
      />
      <PopoverCard
      mainText={pickedGiveUpPlayer
        ? `Are you sure you want to skip? ${pickedGiveUpPlayer === playerOne ? playerTwo.name : playerOne.name} will choose any previous dare for you, for double the time!`
        : ''}
      open={replayGiveUpConfirmOpen}
      onClose={() => { setReplayGiveUpConfirmOpen(false); setPreTimer(3); setIsTimerRunning(true); }}
      showButtonOne={true}
      textButtonOne="Yes, Skip"
      onButtonOne={() => { setReplayGiveUpConfirmOpen(false); if (onGiveUpReplay) onGiveUpReplay(pickedGiveUpPlayer === playerOne ? playerTwo : playerOne); }}
      />
      <PopoverCard
      mainText={"Are you sure you want to start the clock again?"}
      open={restartConfirmPopover}
      onClose={() => setRestartConfirmPopover(false)}
      showButtonOne={true}
      textButtonOne="Yes, Start Again!"
      onButtonOne={() => { setRestartConfirmPopover(false); resetClock(duration); }}
      showButtonTwo={true}
      textButtonTwo="No"
      onButtonTwo={() => setRestartConfirmPopover(false)}
      hideBack={true}
      />
        <Box minHeight={"90px"}>
        {showButtons &&
        <Box display={'flex'} alignItems='center' justifyContent={'center'}>
          <BaseButton onClick={() => setRestartConfirmPopover(true)}> Restart Clock</BaseButton>
          {!hideGiveUp && <BaseButton onClick={handleGiveUp}> Give Up</BaseButton>}
          </Box>
        }
        </Box>
    </CustomContainer>
  );
};

export default CountdownTimer;